import { type Components, type ComponentsVariants, svgIconClasses, type Theme } from '@mui/material'
import type { Interpolation } from '@mui/system'
import { darken } from '@mui/material/styles'

type VariantOverrideRecord<T extends string> = {
    [K in T]: true
}

type CybusButtonVariants = VariantOverrideRecord<
    'primary' | 'secondary' | 'primaryOutline' | 'primaryGhost' | 'ghost' | 'danger' | 'dangerOutline' | 'dangerGhost' | 'legacyAmber'
>

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides extends CybusButtonVariants {
        // Deprecates default MUI variants
        text: false
        contained: false
        outlined: false
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonOwnProps {
        /**
         * TODO: make mandatory and migrate MUI's IconButton usages
         * @see https://cybusio.atlassian.net/browse/CC-4499
         */
        variant?: keyof CybusButtonVariants
    }
}

const variants = <T extends 'MuiButton' | 'MuiIconButton'>(): ComponentsVariants<Theme>[T] =>
    [
        {
            props: { variant: 'primary' },
            style: ({ theme }) => ({
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary[700],
                '&:hover': {
                    backgroundColor: theme.palette.primary[800],
                },
                '&:active': {
                    backgroundColor: theme.palette.primary[900],
                },
                '&:disabled': {
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.primary[700],
                },
            }),
        },
        {
            props: { variant: 'secondary' },
            style: ({ theme }) => ({
                color: theme.palette.grey[700],
                backgroundColor: theme.palette.common.white,
                borderColor: theme.palette.grey[300],
                '&:hover': {
                    backgroundColor: theme.palette.grey[50],
                },
                '&:active': {
                    backgroundColor: theme.palette.grey[100],
                    borderColor: theme.palette.grey[400],
                },
                '&:disabled': {
                    color: theme.palette.grey[700],
                },
            }),
        },
        {
            props: { variant: 'primaryOutline' },
            style: ({ theme }) => ({
                color: theme.palette.primary[700],
                backgroundColor: theme.palette.common.white,
                borderColor: theme.palette.primary[300],
                '&:hover': {
                    backgroundColor: theme.palette.primary[100],
                },
                '&:active': {
                    color: theme.palette.primary[800],
                    backgroundColor: theme.palette.primary[200],
                    borderColor: theme.palette.primary[400],
                },
                '&:disabled': {
                    color: theme.palette.primary[700],
                },
            }),
        },
        {
            props: { variant: 'primaryGhost' },
            style: ({ theme }) => ({
                color: theme.palette.primary[700],
                '&:hover': {
                    backgroundColor: theme.palette.grey[50],
                },
                '&:active': {
                    backgroundColor: theme.palette.grey[100],
                },
                '&:disabled': {
                    color: theme.palette.primary[500],
                },
            }),
        },
        {
            props: { variant: 'ghost' },
            style: ({ theme }) => ({
                color: theme.palette.grey[600],
                '&:hover': {
                    color: theme.palette.grey[700],
                    backgroundColor: theme.palette.grey[100],
                },
                '&:active': {
                    color: theme.palette.grey[800],
                    backgroundColor: theme.palette.grey[200],
                },
                '&:disabled': {
                    color: theme.palette.grey[600],
                },
            }),
        },
        {
            props: { variant: 'danger' },
            style: ({ theme }) => ({
                color: theme.palette.common.white,
                backgroundColor: theme.palette.error[600],
                '&:hover': {
                    backgroundColor: theme.palette.error[700],
                },
                '&:active': {
                    backgroundColor: theme.palette.error[800],
                },
                '&:disabled': {
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.error[600],
                },
            }),
        },
        {
            props: { variant: 'dangerOutline' },
            style: ({ theme }) => ({
                color: theme.palette.error[700],
                backgroundColor: theme.palette.common.white,
                borderColor: theme.palette.error[300],
                '&:hover': {
                    backgroundColor: theme.palette.error[50],
                    borderColor: theme.palette.error[400],
                },
                '&:active': {
                    color: theme.palette.error[800],
                    backgroundColor: theme.palette.error[100],
                    borderColor: theme.palette.error[500],
                },
                '&:disabled': {
                    color: theme.palette.error[700],
                },
            }),
        },
        {
            props: { variant: 'dangerGhost' },
            style: ({ theme }) => ({
                color: theme.palette.error[600],
                '&:hover': {
                    color: theme.palette.error[700],
                    backgroundColor: theme.palette.error[50],
                },
                '&:active': {
                    color: theme.palette.error[800],
                    backgroundColor: theme.palette.error[100],
                },
                '&:disabled': {
                    color: theme.palette.error[600],
                },
            }),
        },
        /**
         * TODO: delete legacyAmber variant
         * @see https://cybusio.atlassian.net/browse/CC-4243
         */
        {
            props: { variant: 'legacyAmber' },
            style: ({ theme }) => ({
                color: theme.palette.common.white,
                backgroundColor: theme.palette.secondary.main,
                '&:hover': {
                    backgroundColor: darken(theme.palette.secondary.main, 0.1),
                },
                '&:active': {
                    backgroundColor: darken(theme.palette.secondary.main, 0.2),
                },
                '&:disabled': {
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.secondary.main,
                },
            }),
        },
    ] satisfies {
        props: { variant: keyof CybusButtonVariants }
        style: Interpolation<{ theme: Theme }>
    }[] as ComponentsVariants<Theme>[T]

export const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
    defaultProps: {
        // disables ripple effect for all buttons
        disableRipple: true,
    },
}

export const MuiButton: Components<Theme>['MuiButton'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.caption,
            letterSpacing: 0,
            fontWeight: 400,
            textTransform: 'none',
            minWidth: 'unset',
            border: '1px solid transparent',
            '&:disabled': { opacity: 0.4 },
        }),
        sizeSmall: ({ theme }) => ({
            minHeight: 24,
            padding: theme.spacing(0, 2),
            [`& .${svgIconClasses.root}`]: {
                height: 12,
                width: 12,
            },
        }),
        sizeMedium: ({ theme }) => ({
            minHeight: 28,
            padding: theme.spacing(0, 3),
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        }),
        sizeLarge: ({ theme }) => ({
            minHeight: 32,
            padding: theme.spacing(0, 4),
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        }),
    },
    variants: variants<'MuiButton'>(),
}

export const MuiIconButton: Components<Theme>['MuiIconButton'] = {
    styleOverrides: {
        root: {
            border: '1px solid transparent',
            borderRadius: 4,
            padding: 0,
            '&:disabled': { opacity: 0.4 },
        },
        sizeSmall: {
            height: 24,
            width: 24,
            [`& .${svgIconClasses.root}`]: {
                height: 12,
                width: 12,
            },
        },
        sizeMedium: {
            height: 28,
            width: 28,
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        },
        sizeLarge: {
            height: 32,
            width: 32,
            [`& .${svgIconClasses.root}`]: {
                height: 16,
                width: 16,
            },
        },
    },
    variants: variants<'MuiIconButton'>(),
}
